import { cn } from '../../lib/utils';

export interface DateRangeOption<V extends string | number = string> {
  value: V;
  label: string;
}

interface DateRangeFilterProps<V extends string | number> {
  value: V;
  options: ReadonlyArray<DateRangeOption<V>>;
  onChange: (value: V) => void;
  label?: string;
  className?: string;
}

/** Segmented control for time-window presets (7d / 30d / 90d, 1h / 24h …). */
export default function DateRangeFilter<V extends string | number>({
  value,
  options,
  onChange,
  label,
  className,
}: DateRangeFilterProps<V>) {
  return (
    <div className={cn('flex items-center gap-2', className)}>
      {label && <span className="text-xs uppercase tracking-wide text-glass-text-3">{label}</span>}
      <div
        role="group"
        aria-label={label ?? 'Time range'}
        className="inline-flex h-9 items-center rounded-lg border border-glass bg-glass-2 p-1"
      >
        {options.map((opt) => {
          const active = opt.value === value;
          return (
            <button
              key={String(opt.value)}
              type="button"
              aria-pressed={active}
              onClick={() => onChange(opt.value)}
              className={cn(
                'rounded-md px-3 py-1 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent2/50',
                active
                  ? 'bg-glass-3 text-accent2 shadow-sm border border-glass'
                  : 'text-glass-text-2 hover:bg-glass-3/60 hover:text-glass-text'
              )}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
